import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { IconButton } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useRecoilState } from "recoil";
import { userInfoState } from "../global-state";
import ErrorIcon from "@mui/icons-material/Error";
import {
  disableAutomation,
  enableAutomation,
  listTwinAutomations,
} from "./automation-service";
import { useTheme } from "@mui/material/styles";
import { useToast } from "../common/toast";
import useApiCall from "../common/api-call";
import dayjs from "dayjs";
import { customFormat } from "../common/custom-format";
import MSwitch from "../common/switch";

const AutomationTwinListView = () => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const cardId = queryParams.get("cardId");
  const { apiCall } = useApiCall();
  const theme = useTheme();
  const { successToast, errorToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [automations, setAutomations] = useState([]);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [isRunning, setIsRunning] = useState("");

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    handleListAutomations();
  }, [cardId]);

  const handleListAutomations = async () => {
    setLoading(true);
    try {
      if (!cardId) {
        console.error("CardId is undefined");
        setAutomations([]);
        setLoading(false);
        return;
      }

      const resp = await listTwinAutomations(
        apiCall,
        userInfo.activeScope.id,
        cardId
      );
      if (resp && Array.isArray(resp.automations)) {
        setAutomations(resp.automations);
        setLoading(false);
      } else {
        setAutomations([]);
      }
    } catch (error) {
      console.error("Failed to fetch automations:", error);
      setAutomations([]);
    } finally {
      setLoading(false);
    }
  };

  const openOverview = (data) => {
    history.push(`/automation/overview?id=${data.id}`);
  };

  const handleEnableDisable = async (event, enableDisable, automationId) => {
    if (enableDisable) {
      const success = await enableAutomation(
        apiCall,
        userInfo.activeScope.id,
        automationId
      );
      if (success) {
        successToast("Enabled.");
        const na = [...automations];
        na.filter((a) => a.id === automationId).forEach(
          (a) => (a.enabled = enableDisable)
        );
        setAutomations(na);
      } else {
        errorToast("Error to enable the automation.");
      }
    } else {
      const success = await disableAutomation(
        apiCall,
        userInfo.activeScope.id,
        automationId
      );
      if (success) {
        successToast("Disabled.");
        const na = [...automations];
        na.filter((a) => a.id === automationId).forEach(
          (a) => (a.enabled = enableDisable)
        );
        setAutomations(na);
      } else {
        errorToast("Error to disable the automation.");
      }
    }
  };

  return (
    <>
      {loading ? (
        <></>
      ) : (
        <Box sx={{ flexGrow: 1, m: 2, pb: 12 }}>
          {!cardId ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                width: "100%",
              }}
            >
              <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                No card selected
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{
                  whiteSpace: "pre-line",
                  width: "100%",
                }}
              >
                Please select a card to view its automations
              </Typography>
            </Box>
          ) : automations.length !== 0 ? (
            <Grid
              container
              spacing={{ xs: 1, md: 2 }}
              columns={{ xs: 2, sm: 4, md: 12 }}
            >
              {automations.map((automation, index) => (
                <Grid item xs={2} sm={2} md={4} key={index}>
                  <Card elevation={0}>
                    <CardActionArea
                      onClick={() => openOverview(automation)}
                      style={{
                        backgroundColor: theme.palette.background.card,
                        position: "relative",
                      }}
                    >
                      <CardContent sx={{ padding: "8px" }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            gutterBottom
                            variant="body1"
                            component="div"
                            sx={{
                              display: "-webkit-box",
                              WebkitBoxOrient: "vertical",
                              WebkitLineClamp: 3,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxHeight: "4.5em",
                              marginBottom: "0px",
                            }}
                          >
                            {automation.name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {!automation.runnable ? (
                              <ErrorIcon
                                style={{
                                  color: "red",
                                  marginRight: "15px",
                                  marginLeft: "15px",
                                }}
                              />
                            ) : (
                              <>
                                <MSwitch
                                  checked={automation.enabled}
                                  disabled={!automation.runnable}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e, b) =>
                                    handleEnableDisable(e, b, automation.id)
                                  }
                                  inputProps={{ "aria-label": "controlled" }}
                                />
                              </>
                            )}
                          </Box>
                        </Box>
                        {automation.timestamp !== 0 && (
                          <Box>
                            <Typography
                              variant="caption"
                              component="div"
                              sx={{
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                marginTop: "5px",
                                color: theme.palette.text.gray,
                              }}
                            >
                              {customFormat(
                                dayjs(automation.timestamp),
                                currentTime
                              )}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                width: "100%",
              }}
            >
              <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                No automations found
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{
                  whiteSpace: "pre-line",
                  width: "100%",
                }}
              >
                Use + button above to create new one
              </Typography>
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{
                  whiteSpace: "pre-line",
                  width: "100%",
                }}
              >
                Or ask the chatbot
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default AutomationTwinListView;
